import { TriangleAlertIcon } from "lucide-react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

interface DialogoCambiosSinGuardarProps {
  abierto: boolean;
  /** Qué se pierde exactamente al salir. Lo pone la pantalla, que lo sabe. */
  descripcion: string;
  /** Se está guardando: bloquea las tres salidas mientras el pedido está en vuelo. */
  guardando?: boolean;
  /**
   * Sin esta prop el aviso ofrece solo cancelar o descartar. Es el caso de un
   * formulario que todavía no tiene dónde guardarse (un alta a medio llenar no
   * puede persistirse sin inventarle un estado que el backend no acepta).
   */
  onGuardarYSalir?: () => void;
  /**
   * Rótulo del botón de guardar. "Guardar y salir" solo es cierto cuando la
   * persona deja el módulo; en un cambio de pestaña sigue en la misma
   * pantalla, y ahí la pantalla pasa "Guardar y continuar".
   */
  rotuloGuardar?: string;
  onSalirSinGuardar: () => void;
  onCancelar: () => void;
}

/**
 * Aviso de salida con cambios sin guardar (regla transversal de UI,
 * `prototipado/fase1-spec.md` §13: "advierte y ofrece guardar o salir
 * descartando").
 *
 * Es `AlertDialog` y no `Dialog` a propósito: Radix no lo cierra al hacer clic
 * afuera. Un cuadro que pregunta "¿descartás lo que escribiste?" y se cierra
 * solo con un clic distraído deja al usuario sin saber qué respondió.
 *
 * Las tres salidas están en orden de daño creciente hacia la izquierda, y la
 * acción segura —guardar— es la única con peso visual. "Salir sin guardar" va
 * en outline y no en `destructive` sólido a propósito: es destructiva, pero no
 * es la que queremos que el ojo elija primero.
 */
export function DialogoCambiosSinGuardar({
  abierto,
  descripcion,
  guardando = false,
  onGuardarYSalir,
  rotuloGuardar = "Guardar y salir",
  onSalirSinGuardar,
  onCancelar,
}: DialogoCambiosSinGuardarProps) {
  return (
    <AlertDialog
      open={abierto}
      onOpenChange={(siguiente) => {
        // Escape cierra: es el equivalente a "Cancelar", nunca a descartar.
        if (!siguiente && !guardando) onCancelar();
      }}
    >
      <AlertDialogContent className="rounded-xl sm:max-w-[35rem]">
        <AlertDialogHeader>
          <div className="flex items-center gap-3.5">
            <span
              aria-hidden="true"
              className="bg-warning-soft text-warning-soft-foreground flex size-11 shrink-0 items-center justify-center rounded-full"
            >
              <TriangleAlertIcon className="size-5" />
            </span>
            {/* `text-h3` NO va acá: `AlertDialogTitle` pasa el className por un
                `cn`, y tailwind-merge clasifica los tokens de texto propios
                como COLOR — junto a `text-ink` uno de los dos se caería en
                silencio. El `text-lg` del componente ya son los 18px del token;
                lo único que falta es el peso 700. */}
            <AlertDialogTitle className="text-ink font-bold">
              Cambios sin guardar
            </AlertDialogTitle>
          </div>

          <AlertDialogDescription className="text-ink-body text-sm">
            {descripcion}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-1 gap-3 sm:justify-start">
          <AlertDialogCancel disabled={guardando}>Cancelar</AlertDialogCancel>

          <Button
            type="button"
            variant="outline"
            disabled={guardando}
            onClick={onSalirSinGuardar}
            /* Los `hover:` van explícitos: tailwind-merge NO deja que una clase
               sin modificador pise una con modificador, así que el
               `hover:text-accent-foreground` del variant `outline` ganaría. */
            className="text-destructive border-destructive/40 hover:text-destructive hover:border-destructive hover:bg-transparent hover:shadow-[0_0_0_4px_rgba(229,84,75,0.18)]"
          >
            Salir sin guardar
          </Button>

          {onGuardarYSalir ? (
            <Button type="button" disabled={guardando} onClick={onGuardarYSalir}>
              {guardando ? <Spinner /> : null}
              {rotuloGuardar}
            </Button>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
